import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Activity, AlertTriangle, CheckCircle2, PauseCircle, RefreshCw, Power } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Skeleton } from '../../components/ui/Skeleton'
import { EmptyState } from '../../components/EmptyState'
import PullToRefresh from '../../components/PullToRefresh'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess } from '../../lib/owner'
import { supabase } from '../../lib/supabaseClient'
import { performRefresh } from '../../lib/refresh'
import { cn } from '../../lib/utils'

// Per-source health dashboard. Owner-only — the route guard above is
// a UX nicety; the underlying news_sources RLS already keeps the
// columns off-limits to anyone else, so a non-owner who lands here
// just sees zero rows.
//
// Status is derived client-side from the columns fetch-news writes:
//   - disabled               → enabled=false
//   - failing                → consecutive_failures >= 5
//   - degraded               → consecutive_failures > 0 OR no fetch in last 12h
//   - healthy                → otherwise
//
// The page calls news_refresh_source_health() on mount to recompute
// the denormalised articles_last_7d column so the table is current.

const STATUS_STYLES = {
  healthy:  { icon: CheckCircle2, tone: 'text-green-500',   label: 'healthy'  },
  degraded: { icon: AlertTriangle, tone: 'text-amber-500',  label: 'degraded' },
  failing:  { icon: AlertTriangle, tone: 'text-red-500',    label: 'failing'  },
  disabled: { icon: PauseCircle,   tone: 'text-muted-foreground', label: 'disabled' },
}

function deriveStatus(source) {
  if (!source.enabled) return 'disabled'
  if ((source.consecutive_failures ?? 0) >= 5) return 'failing'
  const lastIso = source.last_fetched_at
  const staleMs = lastIso ? Date.now() - new Date(lastIso).getTime() : Infinity
  const STALE_MS = 12 * 3600 * 1000
  if ((source.consecutive_failures ?? 0) > 0) return 'degraded'
  if (staleMs > STALE_MS) return 'degraded'
  return 'healthy'
}

function timeAgo(iso, locale) {
  if (!iso) return ''
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return locale === 'ar' ? 'الآن' : 'now'
  if (mins < 60) return locale === 'ar' ? `${mins}د` : `${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return locale === 'ar' ? `${hours}س` : `${hours}h`
  const days = Math.floor(hours / 24)
  return locale === 'ar' ? `${days}ي` : `${days}d`
}

export function NewsHealthPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const ownerView = hasOwnerAccess(user)
  const locale = i18n.language === 'ar' ? 'ar' : 'en'

  const [sources, setSources] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all') // all / problems / disabled
  // news-enrich manual trigger state. The cron now runs once daily
  // (06:00 UTC); this button lets the owner force a re-tag pass when
  // a batch of articles is sitting un-tagged.
  const [enriching, setEnriching] = useState(false)
  const [enrichResult, setEnrichResult] = useState(null)
  const [enrichError, setEnrichError] = useState(null)
  const handleEnrich = useCallback(async () => {
    if (enriching) return
    setEnriching(true)
    setEnrichResult(null)
    setEnrichError(null)
    try {
      const { data, error } = await supabase.functions.invoke('news-enrich', {
        body: { limit: 200 },
      })
      if (error) throw error
      setEnrichResult(data)
    } catch (e) {
      setEnrichError(e?.message || 'failed')
    } finally {
      setEnriching(false)
    }
  }, [enriching])

  const loadSources = useCallback(async () => {
    // Refresh the 7d article count first so the table is up to date.
    await supabase.rpc('news_refresh_source_health')
    const { data } = await supabase
      .from('news_sources')
      .select('id, slug, name, kind, category, language, enabled, last_fetched_at, consecutive_failures, last_error_message, last_error_at, auto_disabled_at, articles_last_7d')
      .order('name', { ascending: true })
    setSources(data || [])
    setLoading(false)
  }, [])

  useEffect(() => {
    if (!ownerView) {
      setLoading(false)
      return
    }
    loadSources()
  }, [ownerView, loadSources])

  const toggleEnabled = useCallback(async (source) => {
    const next = !source.enabled
    const update = { enabled: next }
    // Re-enabling a source clears the failure history so the cron
    // gives it a fresh start. Disabling preserves whatever was there.
    if (next) {
      update.consecutive_failures = 0
      update.auto_disabled_at = null
      update.last_error_message = null
      update.last_error_at = null
    }
    // Optimistic local flip so the row state updates instantly even
    // when supabase swallows an RLS rejection silently. The refetch
    // below corrects state if the write didn't land.
    setSources((prev) => prev.map((s) => (
      s.id === source.id ? { ...s, ...update } : s
    )))
    const { error, count } = await supabase
      .from('news_sources')
      .update(update, { count: 'exact' })
      .eq('id', source.id)
    if (error) {
      // Surface to console so RLS / network issues aren't invisible
      // — the user reported toggles "not working" because there was
      // no feedback path when the write failed.
      // eslint-disable-next-line no-console
      console.error('[news_sources.update]', error)
    } else if (count === 0) {
      // No matching row updated — almost always RLS rejecting the
      // write (e.g. missing UPDATE policy / wrong owner uuid).
      // eslint-disable-next-line no-console
      console.warn('[news_sources.update] update returned count=0 — RLS likely rejected')
    }
    loadSources()
  }, [loadSources])

  const handleRefresh = useCallback(() => performRefresh([loadSources]), [loadSources])

  if (!ownerView) {
    return (
      <div className="space-y-3 content-fade pb-8">
        <Header onBack={() => navigate('/news')} t={t} />
        <EmptyState
          icon={Activity}
          title={t('news.health.notOwnerTitle')}
          description={t('news.health.notOwnerDescription')}
        />
      </div>
    )
  }

  const filtered = sources.filter((s) => {
    if (filter === 'disabled') return !s.enabled
    if (filter === 'problems') {
      return !s.enabled || (s.consecutive_failures ?? 0) > 0
    }
    return true
  })

  const counts = {
    total: sources.length,
    healthy: sources.filter((s) => deriveStatus(s) === 'healthy').length,
    degraded: sources.filter((s) => deriveStatus(s) === 'degraded').length,
    failing: sources.filter((s) => deriveStatus(s) === 'failing').length,
    disabled: sources.filter((s) => !s.enabled).length,
  }

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-3 content-fade pb-8">
        <Header onBack={() => navigate('/news')} t={t} />

        <div className="grid grid-cols-4 gap-2">
          <StatCard label={t('news.health.healthy')} value={counts.healthy} tone="text-green-500" />
          <StatCard label={t('news.health.degraded')} value={counts.degraded} tone="text-amber-500" />
          <StatCard label={t('news.health.failing')} value={counts.failing} tone="text-red-500" />
          <StatCard label={t('news.health.disabled')} value={counts.disabled} tone="text-muted-foreground" />
        </div>

        {/* Manual "Refresh tags" trigger. Calls news-enrich on demand
            (the cron only runs once daily now) so the owner can force
            a tagging pass after a big inflow of new articles. */}
        <Card>
          <CardContent className="p-3 flex items-center gap-2">
            <button
              type="button"
              onClick={handleEnrich}
              disabled={enriching}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-input transition-colors',
                enriching ? 'opacity-60' : 'hover:bg-accent',
              )}
              dir="auto"
            >
              <RefreshCw className={cn('w-3.5 h-3.5', enriching && 'animate-spin')} />
              {enriching
                ? t('news.health.enriching')
                : t('news.health.refreshTags')}
            </button>
            <div className="flex-1 min-w-0">
              {enrichError ? (
                <p className="text-[11px] text-red-600 truncate" dir="auto">{enrichError}</p>
              ) : enrichResult ? (
                <p className="text-[11px] text-muted-foreground truncate" dir="auto">
                  {t('news.health.refreshTagsResult', {
                    tagged: enrichResult.tagged ?? 0,
                    processed: enrichResult.processed ?? 0,
                    providers: (enrichResult.providers || []).join(', ') || '—',
                  })}
                </p>
              ) : (
                <p className="text-[11px] text-muted-foreground truncate" dir="auto">
                  {t('news.health.refreshTagsHint')}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1">
          {['all', 'problems', 'disabled'].map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              aria-pressed={filter === f}
              className={cn(
                'px-3 py-1.5 rounded-full text-xs whitespace-nowrap border transition-colors',
                filter === f
                  ? 'border-app-primary-500 bg-app-primary-500/10 text-app-primary-500'
                  : 'border-input text-muted-foreground hover:bg-accent',
              )}
            >
              {t(`news.health.filter.${f}`)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState icon={Activity} title={t('news.health.emptyFilter')} />
        ) : (
          <div className="space-y-2">
            {filtered.map((source) => (
              <SourceRow
                key={source.id}
                source={source}
                locale={locale}
                onToggle={toggleEnabled}
                t={t}
              />
            ))}
          </div>
        )}
      </div>
    </PullToRefresh>
  )
}

function Header({ onBack, t }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <button
        type="button"
        onClick={onBack}
        aria-label={t('settings.back')}
        className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
      >
        <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
      </button>
      <div className="flex-1 min-w-0">
        <h1 className="text-xl font-bold text-foreground flex items-center gap-2" dir="auto">
          <Activity className="w-5 h-5 text-app-primary-500" />
          {t('news.health.title')}
        </h1>
      </div>
    </div>
  )
}

function StatCard({ label, value, tone }) {
  return (
    <Card>
      <CardContent className="p-2 text-center">
        <p className={cn('text-lg font-bold font-mono', tone)} dir="ltr">{value}</p>
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground" dir="auto">{label}</p>
      </CardContent>
    </Card>
  )
}

function SourceRow({ source, locale, onToggle, t }) {
  const status = deriveStatus(source)
  const styles = STATUS_STYLES[status]
  const StatusIcon = styles.icon
  return (
    <Card>
      <CardContent className="p-3">
        <div className="flex items-start gap-2">
          <StatusIcon className={cn('w-4 h-4 flex-shrink-0 mt-0.5', styles.tone)} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground" dir="auto">
              {source.name}
            </p>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground mt-0.5" dir="auto">
              <span className="font-mono uppercase">{source.kind}</span>
              <span>·</span>
              <span>{source.category}</span>
              <span>·</span>
              <span>{source.language}</span>
              <span>·</span>
              <span>
                {t('news.health.articles7d', { count: source.articles_last_7d || 0 })}
              </span>
              {source.last_fetched_at && (
                <>
                  <span>·</span>
                  <span dir="ltr">{t('news.health.lastFetched', { time: timeAgo(source.last_fetched_at, locale) })}</span>
                </>
              )}
              {(source.consecutive_failures || 0) > 0 && (
                <>
                  <span>·</span>
                  <span className="text-red-500" dir="ltr">
                    {t('news.health.failures', { count: source.consecutive_failures })}
                  </span>
                </>
              )}
            </div>
            {source.last_error_message && (
              <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-1 font-mono break-words" dir="ltr">
                {source.last_error_message}
              </p>
            )}
          </div>
          <Button
            type="button"
            variant={source.enabled ? 'ghost' : 'outline'}
            size="sm"
            onClick={() => onToggle(source)}
            className="flex-shrink-0"
            aria-label={t(source.enabled ? 'news.health.disable' : 'news.health.enable')}
          >
            <Power className={cn('w-3.5 h-3.5 me-1', source.enabled ? 'text-green-500' : 'text-muted-foreground')} />
            <span className="text-xs">{t(source.enabled ? 'news.health.disable' : 'news.health.enable')}</span>
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
